import React, { useEffect, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Link from '@material-ui/core/Link';
import Grid from '@material-ui/core/Grid';
import DatePicker from '@material-ui/lab/DatePicker';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import { useAPI } from '../../utils/api';
import { useDialog } from '../../utils/dialog';
import { makeStyles } from '@material-ui/styles';
import { styled } from '@material-ui/core';
import PropTypes from 'prop-types';

const useStyles = makeStyles({
  root: {
    '&:last-of-type td': {
      borderBottom: 'none',
    },
  },
});

const MemberItemContainer = styled(Grid)({
  textAlign: 'center',
  borderLeft: '1px solid',
});

const MemberItemGrid = styled(Grid)({
  padding: 6,
  borderBottom: '1px solid',
  borderRight: '1px solid',
  display: 'block',
  alignItems: 'center',
  justifyContent: 'center',
  overflowWrap: 'anywhere',
});

const ParsedCell = ({ text }) => {
  return (
    <>
      <Box>{text}</Box>
    </>
  );
};

ParsedCell.propTypes = {
  // text: PropTypes.oneOfType([PropTypes.string]),
  text: PropTypes.string,
};

ParsedCell.defaultProps = {
  text: '',
};

const columns = [
  { id: 'activityCode', label: '活動編號' },
  { id: 'settingsDate', label: '設定日期' },
  { id: 'startDate', label: '活動起始日' },
  { id: 'endDate', label: '活動結束日' },
  {
    id: 'activityName',
    label: '活動名稱',
  },
  {
    id: 'upStandardFee',
    label: '設定達標金額',
  },
  {
    id: 'notUpStandardFee',
    label: '月結手續費',
  },
  {
    id: 'lastEdit',
    label: '最後修改人',
  },
  {
    id: 'function',
    label: '功能',
    minWidth: '250px',
  },
];

const createData = ({
  activityName,
  activityCode,
  settingsDate,
  startDate,
  endDate,
  upStandardFee,
  notUpStandardFee,
  lastEdit,
  activityId,
  isShowDelete,
}) => {
  const editUrl = `/#/accounting/fee-setting/detail/${activityId || ''}`;

  return {
    activityName,
    activityCode,
    settingsDate,
    startDate,
    endDate,
    upStandardFee,
    notUpStandardFee,
    lastEdit,
    activityId,
    editUrl,
    isShowDelete,
  };
};

const FeeSettingPage = () => {
  const api = useAPI();
  const dialog = useDialog();
  const classes = useStyles();
  const [createTimeValue, setCreateTimeValue] = useState(null);
  const [endTimeValue, setEndTimeValue] = useState(null);
  const [dateFilter, setDateFilter] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setMainData] = useState([]);
  const [isUnmounted, setIsUnmounted] = useState(false);
  const [shouldPageUpdated, setShouldPageUpdated] = useState(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const deleteActivity = (activityId) => {
    const yes = confirm('確定刪除嗎？');

    if (yes) {
      api
        .deleteActivity({ activityId })
        .then(() => {
          dialog.openAlert({ message: '刪除成功' });
          api
            .searchAccountMonthFee(dateFilter)
            .then((feeData) => {
              updateData(feeData);
              setShouldPageUpdated(true);
            })
            .catch(() => {});
        })
        .catch(() => {});
    }
  };

  const updateData = (feeData) => {
    if (!isUnmounted) {
      const rows = [];
      feeData.map((origin) => {
        rows.push(
          createData({
            activityName: origin.activityName,
            activityCode: origin.activityCode,
            settingsDate: origin.settingsDate,
            startDate: origin.startDate,
            endDate: origin.endDate,
            upStandardFee: origin.upStandardFee,
            notUpStandardFee: origin.notUpStandardFee,
            lastEdit: origin.lastEdit,
            activityId: origin.activityId,
            isShowDelete: origin.isShowDelete,
          })
        );
      });
      setMainData(rows);
    }
  };

  const search = (event) => {
    event.preventDefault();
    const form = event.target;
    const formData = new FormData(form);
    if (formData.get('createTime') == null || formData.get('endTime') == null) {
      dialog.openAlert({ message: '設定日期為必填項目' });
      return;
    }
    const date = {
      createDate: formData.get('createTime'),
      endDate: formData.get('endTime'),
    };
    setDateFilter(date);
    api
      .searchAccountMonthFee(date)
      .then((data) => {
        updateData(data);
        setShouldPageUpdated(true);
      })
      .catch(() => {});
  };

  useEffect(() => {
    return () => {
      setShouldPageUpdated(false);
    };
  }, [shouldPageUpdated]);

  useEffect(() => {
    const dateNow = new Date();
    dateNow.setDate(dateNow.getDate() - 30);
    setCreateTimeValue(dateNow);
    setEndTimeValue(new Date());

    const date = {
      createDate: dateNow,
      endDate: new Date(),
    };
    api
      .searchAccountMonthFee(date)
      .then((data) => {
        updateData(data);
      })
      .catch(() => {});

    return () => {
      setIsUnmounted(true);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Paper
        elevation={4}
        sx={{
          paddingTop: { xs: 1, md: 2 },
          paddingBottom: { xs: 2, md: 4 },
          marginBottom: 4,
          maxWidth: '900px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        <Container>
          <Typography variant="h6" gutterBottom display="inline" mr="2px">
            查詢條件
          </Typography>
          <Box component="form" noValidate onSubmit={search}>
            <Grid container spacing={{ xs: 1, md: 2 }}>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="結帳起始日(查詢期間最多90天)"
                  value={createTimeValue}
                  onChange={(newValue) => {
                    setCreateTimeValue(newValue);
                  }}
                  mask="____-__-__"
                  inputFormat={'yyyy-MM-dd'}
                  clearable={true}
                  clearText="清除"
                  cancelText="取消"
                  okText="確定"
                  renderInput={(parameters) => (
                    <TextField
                      id="createTime"
                      name="createTime"
                      label="結帳起始日"
                      fullWidth
                      variant="standard"
                      size="small"
                      autoComplete="none"
                      {...parameters}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="結帳結束日"
                  value={endTimeValue}
                  onChange={(newValue) => {
                    setEndTimeValue(newValue);
                  }}
                  mask="____-__-__"
                  inputFormat={'yyyy-MM-dd'}
                  clearable={true}
                  clearText="清除"
                  cancelText="取消"
                  okText="確定"
                  renderInput={(parameters) => (
                    <TextField
                      id="endTime"
                      name="endTime"
                      label="結帳結束日"
                      fullWidth
                      variant="standard"
                      size="small"
                      autoComplete="none"
                      {...parameters}
                    />
                  )}
                />
              </Grid>

              <Grid
                container
                spacing={3}
                sx={{ margin: 'auto', width: '100%', justifyContent: 'center' }}
              >
                <Grid item xs={10} sm={4} sx={{ maxWidth: '200px' }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                  >
                    查詢
                  </Button>
                </Grid>
                <Grid item xs={10} sm={4} sx={{ maxWidth: '200px' }}>
                  <Button
                    type="button"
                    fullWidth
                    variant="contained"
                    color="warning"
                    onClick={() => {
                      location.href = '/#/accounting/fee-setting/new';
                    }}
                    sx={{ color: 'white' }}
                  >
                    新增活動
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Paper>
      <Typography variant="body2" marginTop={2} marginBottom={2}>
        提醒: 活動起始日~結束日，符合在這區間內的【結帳日】，才可享有此優惠。
      </Typography>
      <Typography variant="body2" marginBottom={1} textAlign="center">
        共找到 {data ? data.length : 0} 筆資料
      </Typography>
      <Paper
        sx={{ width: '100%', overflow: 'hidden', padding: 1 }}
        elevation={5}
      >
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow
                sx={{
                  display: { xs: 'none', sm: 'table-row' },
                }}
              >
                {columns
                  ? columns.map((column, index) => (
                      <TableCell
                        key={`${column.id}-${index}`}
                        align={column.align || 'center'}
                        sx={{
                          minWidth: column.minWidth || 'none',
                          padding: 1,
                          maxWidth: '100%',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))
                  : undefined}
              </TableRow>
            </TableHead>

            <TableBody>
              {data && !shouldPageUpdated
                ? data
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, rowIndex) => {
                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={`${rowIndex}`}
                          className={classes.root}
                        >
                          {data && columns
                            ? columns.map((column, columnIndex) => {
                                return (
                                  <TableCell
                                    key={`${column.id}-${columnIndex}`}
                                    align={column.align || 'center'}
                                    sx={{
                                      display: {
                                        xs: 'none',
                                        sm: 'table-cell',
                                      },
                                      padding: 1,
                                      minHeight: '0',
                                      maxWidth: '100%',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {column.id === 'function' ? (
                                      <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                          <Link
                                            underline="none"
                                            href={row.editUrl}
                                            sx={{ textDecoration: 'none' }}
                                          >
                                            <Button
                                              type="button"
                                              fullWidth
                                              variant="contained"
                                              color="primary"
                                              sx={{ color: 'white' }}
                                            >
                                              編輯
                                            </Button>
                                          </Link>
                                        </Grid>

                                        <Grid item xs={6}>
                                          {row.isShowDelete === true ? (
                                            <Button
                                              type="button"
                                              fullWidth
                                              variant="contained"
                                              color={'gray'}
                                              onClick={() =>
                                                deleteActivity(row.activityId)
                                              }
                                              sx={{ color: 'white' }}
                                            >
                                              刪除
                                            </Button>
                                          ) : undefined}
                                        </Grid>
                                      </Grid>
                                    ) : (
                                      <ParsedCell
                                        text={String(row[column.id])}
                                      />
                                    )}
                                  </TableCell>
                                );
                              })
                            : undefined}

                          <TableCell
                            sx={{
                              display: {
                                xs: 'block',
                                sm: 'none',
                                width: '100%',
                              },
                            }}
                          >
                            <MemberItemContainer
                              container
                              borderTop="1px solid"
                              fontWeight="bold"
                            >
                              <MemberItemGrid item xs={12}>
                                活動編號
                              </MemberItemGrid>
                            </MemberItemContainer>
                            <MemberItemContainer container>
                              <MemberItemGrid item xs={12}>
                                <ParsedCell text={row.activityCode} />
                              </MemberItemGrid>
                            </MemberItemContainer>

                            <MemberItemContainer container fontWeight="bold">
                              <MemberItemGrid item xs={12}>
                                設定日期
                              </MemberItemGrid>
                            </MemberItemContainer>
                            <MemberItemContainer container>
                              <MemberItemGrid item xs={12}>
                                <ParsedCell text={row.settingsDate} />
                              </MemberItemGrid>
                            </MemberItemContainer>

                            <MemberItemContainer container fontWeight="bold">
                              <MemberItemGrid item xs={12}>
                                設定活動起日
                              </MemberItemGrid>
                            </MemberItemContainer>
                            <MemberItemContainer container>
                              <MemberItemGrid item xs={12}>
                                <ParsedCell text={row.startDate} />
                              </MemberItemGrid>
                            </MemberItemContainer>

                            <MemberItemContainer container fontWeight="bold">
                              <MemberItemGrid item xs={12}>
                                設定活動結束日
                              </MemberItemGrid>
                            </MemberItemContainer>
                            <MemberItemContainer container>
                              <MemberItemGrid item xs={12}>
                                <ParsedCell text={row.endDate} />
                              </MemberItemGrid>
                            </MemberItemContainer>

                            <MemberItemContainer container fontWeight="bold">
                              <MemberItemGrid item xs={12}>
                                活動名稱
                              </MemberItemGrid>
                            </MemberItemContainer>
                            <MemberItemContainer container>
                              <MemberItemGrid item xs={12}>
                                <ParsedCell text={row.activityName} />
                              </MemberItemGrid>
                            </MemberItemContainer>

                            <MemberItemContainer container fontWeight="bold">
                              <MemberItemGrid item xs={12}>
                                設定達標金額
                              </MemberItemGrid>
                            </MemberItemContainer>
                            <MemberItemContainer container>
                              <MemberItemGrid item xs={12}>
                                <ParsedCell text={String(row.upStandardFee)} />
                              </MemberItemGrid>
                            </MemberItemContainer>

                            <MemberItemContainer container fontWeight="bold">
                              <MemberItemGrid item xs={12}>
                                月結手續費
                              </MemberItemGrid>
                            </MemberItemContainer>
                            <MemberItemContainer container>
                              <MemberItemGrid item xs={12}>
                                <ParsedCell
                                  text={String(row.notUpStandardFee)}
                                />
                              </MemberItemGrid>
                            </MemberItemContainer>

                            <MemberItemContainer container fontWeight="bold">
                              <MemberItemGrid item xs={12}>
                                最後修改人
                              </MemberItemGrid>
                            </MemberItemContainer>
                            <MemberItemContainer container>
                              <MemberItemGrid item xs={12}>
                                <ParsedCell text={row.lastEdit} />
                              </MemberItemGrid>
                            </MemberItemContainer>
                            <MemberItemContainer container>
                              <Grid
                                item
                                container
                                xs={12}
                                spacing={1}
                                padding={1}
                                borderBottom="1px solid"
                                borderRight="1px solid"
                                justifyContent="center"
                              >
                                <Grid item xs={4}>
                                  <Link
                                    underline="none"
                                    href={row.editUrl}
                                    sx={{ textDecoration: 'none' }}
                                  >
                                    <Button
                                      type="button"
                                      fullWidth
                                      variant="contained"
                                      color="primary"
                                      sx={{ color: 'white' }}
                                    >
                                      編輯
                                    </Button>
                                  </Link>
                                </Grid>
                                {row.isShowDelete === true ? (
                                  <Grid item xs={4}>
                                    <Button
                                      type="button"
                                      fullWidth
                                      variant="contained"
                                      color={'gray'}
                                      onClick={() =>
                                        deleteActivity(row.activityId)
                                      }
                                      sx={{ color: 'white' }}
                                    >
                                      刪除
                                    </Button>
                                  </Grid>
                                ) : undefined}
                              </Grid>
                            </MemberItemContainer>
                          </TableCell>
                        </TableRow>
                      );
                    })
                : undefined}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          labelRowsPerPage="每頁顯示"
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={data ? data.length : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  );
};

export default FeeSettingPage;
